import { useState } from 'react';
import { ChevronLeft, ChevronRight, Calendar, Clock, Building2, GraduationCap, CalendarDays, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

// Données du planning
const planningData = {
  // Jours CFA (lundis)
  cfaDays: [
    "2026-09-07", "2026-09-14", "2026-09-21", "2026-09-28",
    "2026-10-05", "2026-10-12", "2026-10-26",
    "2026-11-02", "2026-11-09", "2026-11-16", "2026-11-23", "2026-11-30",
    "2026-12-07", "2026-12-14",
    "2027-01-04", "2027-01-11", "2027-01-18", "2027-01-25",
    "2027-02-01", "2027-02-08", "2027-02-22",
    "2027-03-01", "2027-03-08", "2027-03-15", "2027-03-22", "2027-03-29",
    "2027-04-05", "2027-04-12", "2027-04-26",
    "2027-05-03", "2027-05-10", "2027-05-17", "2027-05-31",
    "2027-06-07", "2027-06-14", "2027-06-21", "2027-06-28",
    "2027-07-05"
  ],
  // Vacances
  vacations: {
    "Toussaint": { start: "2026-10-19", end: "2026-10-30" },
    "Noël": { start: "2026-12-21", end: "2027-01-01" },
    "Hiver": { start: "2027-02-15", end: "2027-03-05" },
    "Printemps": { start: "2027-04-19", end: "2027-05-04" },
    "Été": { start: "2027-07-06", end: "2027-07-31" }
  },
  // Évaluations importantes
  evaluations: {
    "2026-11-16": "Éval. formative CCP1",
    "2027-01-18": "MSP blanche CCP1",
    "2027-02-08": "Questionnement CCP1",
    "2027-04-12": "MSP blanche CCP2",
    "2027-05-17": "Questionnement CCP2",
    "2027-06-14": "Jury blanc"
  },
  // Épreuves finales
  finals: {
    start: "2027-07-05",
    end: "2027-07-11",
    label: "ÉPREUVES FINALES RNCP"
  },
  // Jours fériés
  holidays: {
    "2026-11-11": "Armistice",
    "2026-12-25": "Noël",
    "2027-01-01": "Jour de l'an",
    "2027-04-05": "Lundi de Pâques",
    "2027-05-13": "Ascension",
    "2027-05-24": "Pentecôte"
  },
  // Contenu mensuel
  monthContent: {
    "2026-09": "BC1: Intro NTC • Veille commerciale",
    "2026-10": "BC1: Plan d'actions • Prospection",
    "2026-11": "BC1: CRM • Prospecter un secteur",
    "2026-12": "BC1: Analyses correctives",
    "2027-01": "BC1: Révisions CCP1 + MSP blanche",
    "2027-02": "BC2: Représenter l'entreprise",
    "2027-03": "BC2: Concevoir une proposition TC",
    "2027-04": "BC2: Négociation • Closing",
    "2027-05": "BC2: Optimiser relation client",
    "2027-06": "Révisions générales • Jury blanc",
    "2027-07": "ÉPREUVES FINALES RNCP"
  }
};

// Noms des mois en français
const monthNames = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"
];

const dayNames = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

// Vérifier si une date est dans une période de vacances
const isVacation = (date) => {
  const dateStr = date.toISOString().split('T')[0];
  for (const [name, period] of Object.entries(planningData.vacations)) {
    if (dateStr >= period.start && dateStr <= period.end) {
      return name;
    }
  }
  return null;
};

// Vérifier si c'est un jour CFA
const isCfaDay = (date) => {
  const dateStr = date.toISOString().split('T')[0];
  return planningData.cfaDays.includes(dateStr);
};

// Vérifier si c'est une évaluation
const isEvaluation = (date) => {
  const dateStr = date.toISOString().split('T')[0];
  return planningData.evaluations[dateStr] || null;
};

// Vérifier si c'est un jour férié
const isHoliday = (date) => {
  const dateStr = date.toISOString().split('T')[0];
  return planningData.holidays[dateStr] || null;
};

// Vérifier si c'est pendant les épreuves finales
const isFinals = (date) => {
  const dateStr = date.toISOString().split('T')[0];
  return dateStr >= planningData.finals.start && dateStr <= planningData.finals.end;
};

// Obtenir le nombre de jours dans un mois
const getDaysInMonth = (year, month) => {
  return new Date(year, month + 1, 0).getDate();
};

// Obtenir le premier jour du mois (0 = Dimanche, 1 = Lundi, etc.)
const getFirstDayOfMonth = (year, month) => {
  const day = new Date(year, month, 1).getDay();
  return day === 0 ? 6 : day - 1; // Convertir pour commencer par Lundi
};

export const Planning = () => {
  const [currentDate, setCurrentDate] = useState(new Date(2026, 8, 1)); // Septembre 2026
  
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  
  const monthKey = `${year}-${String(month + 1).padStart(2, '0')}`;
  const monthContent = planningData.monthContent[monthKey] || "";

  const goToPrevMonth = () => {
    const newDate = new Date(year, month - 1, 1);
    if (newDate >= new Date(2026, 8, 1)) {
      setCurrentDate(newDate);
    }
  };

  const goToNextMonth = () => {
    const newDate = new Date(year, month + 1, 1);
    if (newDate <= new Date(2027, 6, 31)) {
      setCurrentDate(newDate);
    }
  };

  const canGoPrev = new Date(year, month - 1, 1) >= new Date(2026, 8, 1);
  const canGoNext = new Date(year, month + 1, 1) <= new Date(2027, 6, 1);

  // Générer les jours du calendrier
  const calendarDays = [];
  
  // Jours vides au début
  for (let i = 0; i < firstDay; i++) {
    calendarDays.push(null);
  }
  
  // Jours du mois
  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(year, month, day);
    const dayOfWeek = date.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    
    calendarDays.push({
      day,
      date,
      isWeekend,
      isCfa: isCfaDay(date),
      vacation: isVacation(date),
      evaluation: isEvaluation(date),
      holiday: isHoliday(date),
      isFinals: isFinals(date),
      isCompany: !isWeekend && dayOfWeek !== 1 && !isVacation(date) && !isHoliday(date)
    });
  }

  return (
    <div data-testid="planning-page">
      {/* Hero Section */}
      <section className="bg-[#0B1F3A] py-16" data-testid="planning-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#C8A75A] mb-4 block">
            Calendrier 2026-2027
          </span>
          <h1 className="font-serif-heading text-4xl sm:text-5xl font-semibold text-white mb-4">
            Planning de formation
          </h1>
          <div className="w-16 h-0.5 bg-[#C8A75A] mx-auto"></div>
        </div>
      </section>

      {/* Calendar Section */}
      <section className="py-12 bg-white" data-testid="calendar-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          {/* Calendar Header */}
          <div className="flex items-center justify-between mb-8">
            <Button
              variant="outline"
              onClick={goToPrevMonth}
              disabled={!canGoPrev}
              className="border-[#0B1F3A] text-[#0B1F3A] hover:bg-[#0B1F3A] hover:text-white rounded-none disabled:opacity-30"
              data-testid="prev-month"
            >
              <ChevronLeft size={20} />
              <span className="hidden sm:inline ml-2">Mois précédent</span>
            </Button>
            
            <div className="text-center">
              <h2 className="font-serif-heading text-3xl sm:text-4xl font-semibold text-[#0B1F3A]">
                {monthNames[month]} {year}
              </h2>
              {monthContent && (
                <p className="text-[#C8A75A] text-sm mt-2 font-medium">{monthContent}</p>
              )}
            </div>
            
            <Button
              variant="outline"
              onClick={goToNextMonth}
              disabled={!canGoNext}
              className="border-[#0B1F3A] text-[#0B1F3A] hover:bg-[#0B1F3A] hover:text-white rounded-none disabled:opacity-30"
              data-testid="next-month"
            >
              <span className="hidden sm:inline mr-2">Mois suivant</span>
              <ChevronRight size={20} />
            </Button>
          </div>

          {/* Calendar Grid */}
          <div className="border border-[#E6E6E6] overflow-hidden">
            {/* Day Headers */}
            <div className="grid grid-cols-7 bg-[#0B1F3A]">
              {dayNames.map((day, index) => (
                <div 
                  key={day}
                  className={`p-3 text-center text-white text-sm font-medium ${index === 0 ? 'bg-[#C8A75A] text-[#0B1F3A]' : ''}`}
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Days */}
            <div className="grid grid-cols-7">
              {calendarDays.map((dayData, index) => {
                if (!dayData) {
                  return <div key={`empty-${index}`} className="p-2 sm:p-4 bg-gray-50 min-h-[60px] sm:min-h-[80px]"></div>;
                }

                const { day, isWeekend, isCfa, vacation, evaluation, holiday, isFinals, isCompany } = dayData;
                
                let bgColor = 'bg-white';
                let textColor = 'text-[#0A0A0A]';
                let badge = null;
                let tooltip = null;

                if (isFinals) {
                  bgColor = 'bg-red-600';
                  textColor = 'text-white';
                  badge = <span className="text-[8px] sm:text-[10px] block mt-1 font-bold">EXAMEN</span>;
                } else if (evaluation) {
                  bgColor = 'bg-orange-500';
                  textColor = 'text-white';
                  badge = <span className="text-[8px] sm:text-[10px] block mt-1 truncate">{evaluation.split(' ')[0]}</span>;
                  tooltip = evaluation;
                } else if (holiday) {
                  bgColor = 'bg-gray-300';
                  textColor = 'text-gray-600';
                  badge = <span className="text-[8px] sm:text-[10px] block mt-1">Férié</span>;
                  tooltip = holiday;
                } else if (vacation) {
                  bgColor = 'bg-purple-100';
                  textColor = 'text-purple-700';
                  badge = <span className="text-[8px] sm:text-[10px] block mt-1">Vacances</span>;
                } else if (isCfa) {
                  bgColor = 'bg-[#0B1F3A]';
                  textColor = 'text-white';
                  badge = <span className="text-[8px] sm:text-[10px] block mt-1 text-[#C8A75A]">CFA</span>;
                } else if (isWeekend) {
                  bgColor = 'bg-gray-100';
                  textColor = 'text-gray-400';
                } else if (isCompany) {
                  bgColor = 'bg-[#C8A75A]/20';
                  textColor = 'text-[#0B1F3A]';
                  badge = <span className="text-[8px] sm:text-[10px] block mt-1 text-[#C8A75A]">Entr.</span>;
                }

                return (
                  <div 
                    key={`day-${day}`}
                    className={`p-2 sm:p-3 min-h-[60px] sm:min-h-[80px] border-t border-l border-[#E6E6E6] ${bgColor} ${textColor} transition-colors relative group`}
                    title={tooltip || ''}
                    data-testid={`day-${day}`}
                  >
                    <div className="font-semibold text-sm sm:text-base">{day}</div>
                    {badge}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Legend */}
          <div className="mt-8 p-6 bg-[#F5F5F5] border border-[#E6E6E6]">
            <h3 className="font-semibold text-[#0B1F3A] mb-4 text-sm uppercase tracking-wide">Légende</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-[#0B1F3A] rounded-sm"></div>
                <span className="text-sm text-[#4A4A4A]">CFA</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-[#C8A75A]/20 border border-[#C8A75A] rounded-sm"></div>
                <span className="text-sm text-[#4A4A4A]">Entreprise</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-purple-100 border border-purple-300 rounded-sm"></div>
                <span className="text-sm text-[#4A4A4A]">Vacances</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-orange-500 rounded-sm"></div>
                <span className="text-sm text-[#4A4A4A]">Évaluation</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-red-600 rounded-sm"></div>
                <span className="text-sm text-[#4A4A4A]">Examen</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-gray-300 rounded-sm"></div>
                <span className="text-sm text-[#4A4A4A]">Férié</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Overview Cards */}
      <section className="py-12 bg-[#F5F5F5]" data-testid="planning-overview">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="bg-white border border-[#E6E6E6] p-4 sm:p-6 text-center">
              <Calendar className="mx-auto text-[#C8A75A] mb-2" size={24} />
              <div className="text-xs text-[#4A4A4A] uppercase tracking-wide mb-1">Début</div>
              <div className="font-semibold text-[#0B1F3A] text-sm">07 Sept. 2026</div>
            </div>
            <div className="bg-white border border-[#E6E6E6] p-4 sm:p-6 text-center">
              <CalendarDays className="mx-auto text-[#C8A75A] mb-2" size={24} />
              <div className="text-xs text-[#4A4A4A] uppercase tracking-wide mb-1">Fin</div>
              <div className="font-semibold text-[#0B1F3A] text-sm">09 Juil. 2027</div>
            </div>
            <div className="bg-white border border-[#E6E6E6] p-4 sm:p-6 text-center">
              <GraduationCap className="mx-auto text-[#C8A75A] mb-2" size={24} />
              <div className="text-xs text-[#4A4A4A] uppercase tracking-wide mb-1">CFA</div>
              <div className="font-semibold text-[#0B1F3A] text-sm">49 lundis (490h)</div>
            </div>
            <div className="bg-white border border-[#E6E6E6] p-4 sm:p-6 text-center">
              <Building2 className="mx-auto text-[#C8A75A] mb-2" size={24} />
              <div className="text-xs text-[#4A4A4A] uppercase tracking-wide mb-1">Entreprise</div>
              <div className="font-semibold text-[#0B1F3A] text-sm">~170 jours</div>
            </div>
            <div className="bg-white border border-[#E6E6E6] p-4 sm:p-6 text-center col-span-2 lg:col-span-1">
              <Clock className="mx-auto text-[#C8A75A] mb-2" size={24} />
              <div className="text-xs text-[#4A4A4A] uppercase tracking-wide mb-1">Rythme</div>
              <div className="font-semibold text-[#0B1F3A] text-sm">1j CFA / 4j Entreprise</div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Dates */}
      <section className="py-12 bg-white" data-testid="key-dates">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <h2 className="font-serif-heading text-2xl font-semibold text-[#0B1F3A] mb-6 text-center">
            Dates clés des examens
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {Object.entries(planningData.evaluations).map(([date, label]) => (
              <div key={date} className="bg-[#F5F5F5] p-4 border-l-4 border-[#C8A75A]">
                <div className="text-xs text-[#C8A75A] font-medium mb-1">
                  {new Date(date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
                </div>
                <div className="text-sm font-medium text-[#0B1F3A]">{label}</div>
              </div>
            ))}
            <div className="bg-red-600 p-4 text-white sm:col-span-2 lg:col-span-4">
              <div className="text-xs font-medium mb-1 opacity-80">
                05-11 Juillet 2027
              </div>
              <div className="font-semibold">ÉPREUVES FINALES RNCP</div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer Note */}
      <section className="py-8 bg-[#0B1F3A]" data-testid="planning-footer">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <p className="text-[#F5F5F5]/60 text-sm">
            Planning indicatif - Zone B (Île-de-France)
          </p>
        </div>
      </section>
    </div>
  );
};

export default Planning;
